import { TileWordmark } from '@words-happen/game-components';
import type { AuthProvider, CountdownInstant, SocialProvider } from '@words-happen/ui-components';
import {
  AuthOptionsModal,
  DailyCountdownCard,
  DEFAULT_AUTH_PROVIDERS,
  PopIn,
  SignInPrompt,
  SocialAuthRow,
  StatusPill,
  WordsHappenButton,
  WordsHappenDivider,
  withEmail,
} from '@words-happen/ui-components';
import type React from 'react';
import { useState } from 'react';
import { ScreenFrame } from '../screen-frame';
import styles from './landing-screen.module.scss';

export interface LandingScreenProps {
  /** Players online now. `undefined` while loading (space is reserved), `null` when unavailable (hidden). */
  playersCount?: number | null;
  /** When today's daily ends. `undefined` while the schedule loads. */
  dailyEndsAt?: CountdownInstant;
  /** The server's time when `dailyEndsAt` was issued. */
  serverNow?: CountdownInstant;
  /** Play the intro. Pass false once it has played this page load. */
  animateIntro?: boolean;
  /** The providers shown, in order, in the auth row and the auth modal. */
  authProviders?: readonly AuthProvider[];
  /**
   * The social providers that can be used (`enabledAuthProviders`); the others are shown disabled, "coming soon", as on
   * the identity app's /sign-in. Email is always enabled. Every provider when left out.
   */
  enabledProviders?: readonly SocialProvider[];
  /** Locale for the players count; the browser's by default. */
  locale?: string;
  onSignIn: () => void;
  onSelectProvider: (provider: AuthProvider) => void;
  onJustDropIn: () => void;
  onOpenDaily: () => void;
  /** Today's daily has ended: fetch the next schedule. */
  onDailyExpired?: () => void;
  className?: string;
}

const WORDMARK_LINES = ['WORDS', 'HAPPEN'];
const WORDMARK_OFFSETS = [0, 10];

/** Seconds into the intro at which each hero element pops in, after the wordmark's tiles. */
const INTRO = { headline: 1.0, subhead: 1.1, card: 1.2 };

/** "12,438 playing", or null when there is no usable count. */
export const formatPlayersCount = (count: number | null | undefined, locale?: string): string | null => {
  if (typeof count !== 'number' || !Number.isFinite(count) || count < 0) return null;
  return `${new Intl.NumberFormat(locale).format(Math.floor(count))} playing`;
};

const PlayersPill: React.FC<{ count: number | null | undefined; locale?: string }> = ({ count, locale }) => {
  if (count === undefined) {
    // Holds the pill's height while the count loads, so nothing shifts when it arrives.
    return (
      <StatusPill className={styles.pillPlaceholder} dot="var(--wh-coral)">
        <span aria-hidden="true">0 playing</span>
      </StatusPill>
    );
  }

  const label = formatPlayersCount(count, locale);
  return label ? <StatusPill dot="var(--wh-coral)">{label}</StatusPill> : null;
};

/**
 * The signed-out landing screen: get people to create an account, while letting anyone play as a guest.
 * Presentational only — data arrives as props and every action is a callback.
 */
export const LandingScreen: React.FC<LandingScreenProps> = ({
  playersCount,
  dailyEndsAt,
  serverNow,
  animateIntro = true,
  authProviders = DEFAULT_AUTH_PROVIDERS,
  enabledProviders,
  locale,
  onSignIn,
  onSelectProvider,
  onJustDropIn,
  onOpenDaily,
  onDailyExpired,
  className,
}) => {
  const [authOpen, setAuthOpen] = useState(false);

  return (
    <ScreenFrame className={className}>
      <header className={styles.topBar}>
        <PlayersPill count={playersCount} locale={locale} />
        <WordsHappenButton size="sm" className={styles.topSignIn} onClick={onSignIn}>
          Sign in
        </WordsHappenButton>
      </header>

      <main className={styles.main}>
        <section className={styles.hero}>
          <TileWordmark lines={WORDMARK_LINES} size={48} offsets={WORDMARK_OFFSETS} animate={animateIntro} />

          <PopIn play={animateIntro} delay={INTRO.headline}>
            <h1 className={styles.headline}>
              <span className={styles.headlineLine}>Spell big.</span>
              <span className={`${styles.headlineLine} ${styles.headlineAccent}`}>Brag bigger.</span>
            </h1>
          </PopIn>

          <PopIn play={animateIntro} delay={INTRO.subhead}>
            <p className={styles.subhead}>
              A fresh puzzle every day, friends to outscore, and a bot that hates losing.
            </p>
          </PopIn>

          <PopIn play={animateIntro} delay={INTRO.card} className={styles.cardSlot}>
            <DailyCountdownCard
              endsAt={dailyEndsAt}
              serverNow={serverNow}
              onPress={onOpenDaily}
              onExpire={onDailyExpired}
            />
          </PopIn>
        </section>

        <section className={styles.actions} aria-label="Get started">
          <WordsHappenButton variant="coral" size="lg" onClick={() => setAuthOpen(true)}>
            Create free account · <span className={styles.ctaAside}>keep your scores</span>
          </WordsHappenButton>
          <SocialAuthRow
            compact
            providers={authProviders}
            enabledProviders={withEmail(enabledProviders)}
            onSelect={onSelectProvider}
          />
          <WordsHappenDivider label="or" />
          <WordsHappenButton variant="ghost-dashed" onClick={onJustDropIn}>
            Just drop in <span aria-hidden="true">→</span>
          </WordsHappenButton>
          <SignInPrompt className={styles.bottomSignIn} onAction={onSignIn} />
        </section>
      </main>

      <AuthOptionsModal
        open={authOpen}
        providers={authProviders}
        enabledProviders={enabledProviders}
        onClose={() => setAuthOpen(false)}
        onSelect={onSelectProvider}
      />
    </ScreenFrame>
  );
};
